import { Box, Button, FileButton, Group, Image, Modal, SegmentedControl, Select, SimpleGrid, Stack, Text, Textarea } from "@mantine/core"
import { DateTimePicker } from "@mantine/dates"
import { CameraIcon, XIcon } from "@phosphor-icons/react"
import dayjs from "dayjs"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { MediaApi } from "../../apis"
import { message } from "../../utils/message"
import { DeliveryActionButton } from "../delivery/action-controls"
import { Dot } from "../delivery/atoms"
import { useDeliveryActions } from "../delivery/delivery-actions"
import { translateBackendError } from "../delivery/translate-error"
import { useFailedMutations } from "./hooks"

const EXCEPTION_TYPE_VALUES = [
  "NO_RESPONSE", "RECIPIENT_REFUSED", "WRONG_ADDRESS", "ADDRESS_NOT_FOUND",
  "RECIPIENT_NOT_AVAILABLE", "RESCHEDULE_REQUESTED", "PARTIAL_DELIVERY",
  "DAMAGED_GOODS", "WRONG_GOODS", "PAYMENT_FAILED", "ID_VERIFICATION_FAILED",
  "ACCESS_BLOCKED", "WEATHER", "VEHICLE_BREAKDOWN", "DRIVER_INCAPACITATED",
  "SECURITY_INCIDENT", "MISSED_TIME_WINDOW", "CUSTOMS_HOLD", "OTHER",
]

/** BE failed-report.ts PHOTO_REQUIRED_TYPES: these need at least one photo. */
const PHOTO_REQUIRED = new Set(["DAMAGED_GOODS", "WRONG_GOODS", "ACCESS_BLOCKED"])

const ATTRIBUTION_VALUES = ["DRIVER_FAULT", "CUSTOMER_FAULT", "EXTERNAL", "SYSTEM_ERROR", "UNDETERMINED"]

interface Props {
  opened: boolean
  onClose: () => void
  deliveryNoteId: string
  deliveryNoteRef?: string | null
  onReported?: () => void
}

/**
 * «Доголдол бүртгэх» (O111): POST /delivery-note/:id/failed with the fields the BE validates:
 * outcome POSTPONE | FAILED, uploaded photos (required for 3 types), optional reschedule time.
 */
export function ReportFailedDialog({ opened, onClose, deliveryNoteId, deliveryNoteRef, onReported }: Props) {
  const { t } = useTranslation("delivery")
  const mutations = useFailedMutations()
  const { permissionState } = useDeliveryActions()
  const perm = permissionState("reportFailed")

  const [exceptionType, setExceptionType] = useState<string | null>(null)
  const [attributionDraft, setAttributionDraft] = useState<string | null>(null)
  const [outcome, setOutcome] = useState<"FAILED" | "POSTPONE">("FAILED")
  const [rescheduledAt, setRescheduledAt] = useState<string | null>(null)
  const [description, setDescription] = useState("")
  const [notes, setNotes] = useState("")
  const [photoUrls, setPhotoUrls] = useState<string[]>([])
  const [uploading, setUploading] = useState(false)

  const photoRequired = !!exceptionType && PHOTO_REQUIRED.has(exceptionType)
  const missing = !exceptionType
    ? t("manage.report.pickType")
    : photoRequired && photoUrls.length === 0
      ? t("manage.report.photoRequired")
      : outcome === "POSTPONE" && !rescheduledAt
        ? t("manage.report.pickDate")
        : null

  const reset = () => {
    setExceptionType(null)
    setAttributionDraft(null)
    setOutcome("FAILED")
    setRescheduledAt(null)
    setDescription("")
    setNotes("")
    setPhotoUrls([])
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleUpload = async (files: File[]) => {
    if (files.length === 0) return
    setUploading(true)
    try {
      const urls: string[] = []
      for (const file of files) {
        const form = new FormData()
        form.append("file", file)
        const res = await MediaApi.upload(form)
        if (res?.url) urls.push(res.url)
      }
      setPhotoUrls((prev) => [...prev, ...urls])
    } catch (e) {
      message.error(translateBackendError(e))
    } finally {
      setUploading(false)
    }
  }

  const handleSubmit = async () => {
    if (!exceptionType || missing) return
    try {
      await mutations.reportFailed(deliveryNoteId, {
        exceptionType,
        attributionDraft: attributionDraft || null,
        description: description.trim() || null,
        notes: notes.trim() || null,
        photoUrls: photoUrls.length > 0 ? photoUrls : undefined,
        outcome,
        rescheduledAt: outcome === "POSTPONE" && rescheduledAt ? dayjs(rescheduledAt).toISOString() : null,
      })
      message.success(t("manage.report.success"))
      onReported?.()
      handleClose()
    } catch {
      // shown below via mutations.error
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      size={520}
      title={
        <Box>
          <Text fw={650} fz="var(--nw-fz-drawer)">
            {t("failed.reportTitle")}
          </Text>
          {deliveryNoteRef && (
            <Text ff="monospace" fz="var(--nw-fz-mono)" c="var(--nw-text-2)">
              {deliveryNoteRef}
            </Text>
          )}
        </Box>
      }
    >
      <Stack gap="md">
        <Select
          label={t("failed.exceptionTypeLabel")}
          placeholder={t("failed.selectPlaceholder")}
          required
          data={EXCEPTION_TYPE_VALUES.map((value) => ({ value, label: t(`failed.exTypeOption.${value}`) }))}
          value={exceptionType}
          onChange={setExceptionType}
          searchable
        />

        <Box>
          <Text fz="var(--nw-fz-body)" fw={500} c="var(--nw-text-2)" mb={4}>
            {t("manage.report.outcome")}
          </Text>
          <SegmentedControl
            fullWidth
            value={outcome}
            onChange={(v) => setOutcome(v as "FAILED" | "POSTPONE")}
            data={[
              { value: "FAILED", label: t("manage.report.outcomeFailed") },
              { value: "POSTPONE", label: t("manage.report.outcomePostpone") },
            ]}
          />
        </Box>

        {outcome === "POSTPONE" && (
          <DateTimePicker
            label={t("manage.report.rescheduledAt")}
            required
            value={rescheduledAt}
            onChange={(v) => setRescheduledAt(v ? String(v) : null)}
            minDate={new Date()}
            valueFormat="YYYY-MM-DD HH:mm"
          />
        )}

        <Select
          label={t("failed.attributionLabel")}
          placeholder={t("failed.selectOptionalPlaceholder")}
          data={ATTRIBUTION_VALUES.map((value) => ({ value, label: t(`failed.attribution.${value}`) }))}
          value={attributionDraft}
          onChange={setAttributionDraft}
          clearable
        />

        <Textarea
          label={t("failed.descriptionLabel")}
          placeholder={t("failed.descriptionPlaceholder")}
          maxLength={2000}
          minRows={2}
          autosize
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
        />

        <Textarea
          label={t("failed.notesLabel")}
          placeholder={t("failed.notesPlaceholder")}
          maxLength={2000}
          minRows={2}
          autosize
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
        />

        <Box>
          <Group justify="space-between" mb={6}>
            <Text fz="var(--nw-fz-body)" fw={500} c="var(--nw-text-2)">
              {t("manage.report.photos")}
              {photoRequired ? " *" : ""}
            </Text>
            <FileButton onChange={handleUpload} accept="image/*" multiple>
              {(props) => (
                <Button {...props} variant="default" leftSection={<CameraIcon size={16} />} loading={uploading}>
                  {t("manage.report.addPhoto")}
                </Button>
              )}
            </FileButton>
          </Group>
          {photoUrls.length > 0 && (
            <SimpleGrid cols={4} spacing="xs">
              {photoUrls.map((url) => (
                <Box key={url} pos="relative">
                  <Image src={url} h={72} radius="sm" fit="cover" alt="" />
                  <Button
                    size="compact-xs"
                    variant="default"
                    pos="absolute"
                    top={4}
                    right={4}
                    aria-label={t("custody.remove")}
                    onClick={() => setPhotoUrls((prev) => prev.filter((u) => u !== url))}
                  >
                    <XIcon size={12} />
                  </Button>
                </Box>
              ))}
            </SimpleGrid>
          )}
        </Box>

        {mutations.error && (
          <Group gap="sm" wrap="nowrap" align="flex-start" role="alert">
            <Box mt={6}>
              <Dot tone="red" />
            </Box>
            <Text fz="var(--nw-fz-body)">{translateBackendError(mutations.error)}</Text>
          </Group>
        )}

        <Stack gap={4} align="flex-end">
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={handleClose} disabled={mutations.submitting}>
              {t("failed.cancel")}
            </Button>
            <DeliveryActionButton
              state={perm}
              color="dark"
              onClick={handleSubmit}
              loading={mutations.submitting}
              disabledReason={missing ?? undefined}
            >
              {t("failed.submit")}
            </DeliveryActionButton>
          </Group>
          {!perm.allowed && (
            <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
              {perm.reason}
            </Text>
          )}
        </Stack>
      </Stack>
    </Modal>
  )
}
